<template>
  <!-- Show policy review card if there are policy issues -->
  <v-card
    v-if="shouldShowCard"
    class="mt-4"
    color="surface"
    variant="elevated"
    elevation="0"
    style="border: 2px solid #eb3d52">
    <v-card-title class="text-h6">
      <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
      Policy Compliance Alert
      <v-chip v-if="outcomePrediction" class="ml-3" color="error" text-color="white" small>
        Likely to be Denied
      </v-chip>
    </v-card-title>
    <v-card-text>
      <div class="text-body-1 font-weight-semibold mb-3">Summary:</div>
      <ul class="pl-4">
        <li v-for="(item, index) in order.policyReview.summary" :key="index" class="text-body-2 mb-2">
          {{ item }}
        </li>
      </ul>
    </v-card-text>
  </v-card>

  <!-- Checklist policy issues and chart age share one banner -->
  <v-alert
    v-if="policyIssueCount > 0 || chartOutOfWindow"
    type="error"
    variant="tonal"
    class="mt-4"
    prominent
    data-test="policy-compliance-banner">
    <template #prepend>
      <v-icon color="error">mdi-alert-circle</v-icon>
    </template>
    <div class="text-subtitle-1 font-weight-bold">
      {{ bannerTitle }}
    </div>
    <div v-if="policyIssueCount > 0" class="text-body-2">
      {{ policyIssueCount }} answer{{ policyIssueCount > 1 ? "s do" : " does" }} not align with the payer's coverage
      criteria. Review the flagged questions below before submitting.
    </div>
    <div v-if="chartOutOfWindow" class="text-body-2" :class="{ 'mt-2': policyIssueCount > 0 }" data-test="chart-age">
      <template v-if="chartAge.newestNoteDate">
        Newest clinical note is from {{ isoToMmddyyyy(chartAge.newestNoteDate) }} ({{ chartAge.daysOld }} days old).
        Charts older than {{ CHART_AGE_MAX_DAYS }} days are unlikely to be approved, and AI answers may be unreliable.
        Request updated notes before submitting.
      </template>
      <template v-else>No dated clinical note found in the chart.</template>
    </div>
  </v-alert>
</template>

<script setup>
import { computed } from "vue";
import { isoToMmddyyyy } from "@/utils/dateUtils";

// Mirrors CHART_AGE_MAX_DAYS in backend/portal/logic/ai/pa_responder.py, which decides outOfWindow.
const CHART_AGE_MAX_DAYS = 180;

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const order = computed(() => props.order);

const outcomePrediction = computed(() => {
  return order.value.policyReview?.outcomePrediction || null;
});

const shouldShowCard = computed(() => {
  return (
    order.value.policyReview &&
    order.value.policyReview.summary &&
    order.value.policyReview.summary.length &&
    (outcomePrediction.value === "DENY" || outcomePrediction.value === "MIXED")
  );
});

const policyIssueCount = computed(() => {
  const reviews = order.value.checklistPolicyReviews || [];
  const answeredIds = new Set((order.value.checklist || []).map((item) => item.id));
  return reviews.filter((r) => r.alignsWithPolicy === "NO" && answeredIds.has(r.questionId)).length;
});

// Written by autocomplete: { newestNoteDate, daysOld, outOfWindow, reasoning }.
const chartAge = computed(() => order.value.chartAgeCheck || {});
const chartOutOfWindow = computed(() => chartAge.value.outOfWindow === true);

const bannerTitle = computed(() => {
  if (policyIssueCount.value > 0) return "Policy Compliance Issue Detected";
  return chartAge.value.newestNoteDate ? "Chart Too Old for Approval" : "Chart Age Unknown";
});
</script>
